import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CalendarClock, Wrench, Plus } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const CATEGORY_LABEL = {
  maintenance: "Mantención",
  repair: "Reparación",
  service: "Servicio",
  others: "Otros",
};

export default function BookingCreate() {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [workshopId, setWorkshopId] = useState("");
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: "",
    category: "maintenance",
    description: "",
    notes: "",
  });
  const [overflow, setOverflow] = useState({
    enabled: false,
    proposed_start: "",
    proposed_end: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!workshopId) {
      setSlots([]);
      return;
    }
    const today = new Date().toISOString();
    api
      .get(`/workshop-slots?workshop_id=${workshopId}&from=${encodeURIComponent(today)}`)
      .then((r) => setSlots(r.data))
      .catch(() => setSlots([]));
  }, [workshopId]);

  const allowOverflowOnAny = useMemo(
    () => slots.some((s) => s.allow_overflow),
    [slots],
  );

  const bookSlot = async (slot) => {
    if (!form.vehicle_id) {
      toast.error("Selecciona un vehículo");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        workshop_id: workshopId,
        vehicle_id: form.vehicle_id,
        slot_id: slot.id,
        category: form.category,
        description: form.description,
        notes: form.notes,
      });
      toast.success("Solicitud enviada al taller");
      navigate("/mis-reservas");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSubmitting(false);
  };

  const submitOverflow = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) {
      toast.error("Selecciona un vehículo");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        workshop_id: workshopId,
        vehicle_id: form.vehicle_id,
        category: form.category,
        description: form.description,
        notes: form.notes,
        proposed_start: new Date(overflow.proposed_start).toISOString(),
        proposed_end: new Date(overflow.proposed_end).toISOString(),
      });
      toast.success("Solicitud SOBRE CUPO enviada. Queda pendiente de aceptación del taller.");
      navigate("/mis-reservas");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="booking-create-page">
      <PageHeader
        tag="Reservar Cupo"
        title="Agendar con un Taller"
        description="Selecciona un taller y un cupo disponible o solicita un sobre cupo."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="veta-tag block mb-2">Taller</label>
          <select
            value={workshopId}
            onChange={(e) => setWorkshopId(e.target.value)}
            className="w-full h-touch px-3 border border-gray-300"
            data-testid="booking-workshop-select"
          >
            <option value="">Selecciona un taller...</option>
            {workshops.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.rut ? ` · ${w.rut}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="veta-tag block mb-2">Vehículo de tu flota</label>
          <select
            value={form.vehicle_id}
            onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
            className="w-full h-touch px-3 border border-gray-300"
            data-testid="booking-vehicle-select"
          >
            <option value="">Selecciona patente...</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate} · {v.brand} {v.model}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="veta-tag block mb-2">Categoría del servicio</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full h-touch px-3 border border-gray-300"
            data-testid="booking-category-select"
          >
            <option value="maintenance">Mantención</option>
            <option value="repair">Reparación</option>
            <option value="service">Servicio</option>
            <option value="others">Otros</option>
          </select>
        </div>
        <div>
          <label className="veta-tag block mb-2">Descripción breve</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Ej: Mantención 10.000 km"
            className="w-full h-touch px-3 border border-gray-300"
            data-testid="booking-description"
          />
        </div>
      </div>

      {workshopId && (
        <>
          <h2 className="font-display text-2xl mb-3">Cupos disponibles</h2>
          <div className="border border-gray-200 overflow-x-auto mb-8">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <Th>Inicio</Th>
                  <Th>Término</Th>
                  <Th>Tipo</Th>
                  <Th>Disponibilidad</Th>
                  <Th>Categoría</Th>
                  <Th>Sobre cupo</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {slots.map((s) => {
                  const full = s.remaining <= 0;
                  const disabled = full && !s.allow_overflow;
                  return (
                    <tr key={s.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`avail-slot-${s.id}`}>
                      <td className="px-4 py-4 text-sm">{fmtDateTime(s.start_at)}</td>
                      <td className="px-4 py-4 text-sm">{fmtDateTime(s.end_at)}</td>
                      <td className="px-4 py-4 text-xs font-bold uppercase">
                        {s.slot_type === "block" ? "Bloque" : "Slot fijo"}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {s.booked_count}/{s.capacity}{" "}
                        {full && <span className="text-orange-600 font-bold text-xs">(lleno)</span>}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {s.category ? CATEGORY_LABEL[s.category] : "Todas"}
                      </td>
                      <td className="px-4 py-4 text-xs">{s.allow_overflow ? "Sí" : "No"}</td>
                      <td className="px-4 py-4 text-right">
                        <button
                          disabled={disabled || submitting}
                          onClick={() => bookSlot(s)}
                          className="inline-flex items-center gap-1 h-9 px-3 bg-black text-white text-xs font-bold uppercase hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`book-slot-${s.id}`}
                        >
                          <Plus size={14} strokeWidth={2.5} />
                          {full && s.allow_overflow ? "Solicitar sobre cupo" : "Reservar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {slots.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                      Este taller aún no publica cupos.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border border-gray-200 p-6 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl flex items-center gap-2">
                <CalendarClock size={20} strokeWidth={2.5} /> ¿Necesitas un horario fuera de cupo?
              </h3>
              <button
                onClick={() => setOverflow({ ...overflow, enabled: !overflow.enabled })}
                className="text-xs font-bold uppercase tracking-wider text-blue-600 underline"
                data-testid="toggle-overflow"
              >
                {overflow.enabled ? "Cerrar" : "Solicitar sobre cupo"}
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Las solicitudes sobre cupo solo son válidas si el taller las habilita. Quedarán como{" "}
              <b>pendientes</b> hasta que el taller las acepte explícitamente.
            </p>
            {!allowOverflowOnAny && (
              <p className="text-xs text-gray-500 mb-3">
                Nota: actualmente este taller no tiene cupos con sobre cupo habilitado. Igualmente puedes enviar la
                solicitud — el taller decidirá.
              </p>
            )}
            {overflow.enabled && (
              <form onSubmit={submitOverflow} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="overflow-form">
                <div>
                  <label className="veta-tag block mb-2">Inicio propuesto</label>
                  <input
                    type="datetime-local"
                    required
                    value={overflow.proposed_start}
                    onChange={(e) => setOverflow({ ...overflow, proposed_start: e.target.value })}
                    className="w-full h-touch px-3 border border-gray-300"
                    data-testid="overflow-start"
                  />
                </div>
                <div>
                  <label className="veta-tag block mb-2">Término propuesto</label>
                  <input
                    type="datetime-local"
                    required
                    value={overflow.proposed_end}
                    onChange={(e) => setOverflow({ ...overflow, proposed_end: e.target.value })}
                    className="w-full h-touch px-3 border border-gray-300"
                    data-testid="overflow-end"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60"
                    data-testid="submit-overflow-btn"
                  >
                    <Wrench size={16} className="inline mr-2" strokeWidth={2.5} />
                    Enviar solicitud
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}
